import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { PageHero, Reveal, SectionHeading } from '../components/Reveal';
import LeadForm from '../components/LeadForm';
import FinalCTA from '../components/home/FinalCTA';
import { ROADMAP_STEPS } from '../data/content';

const EXAMPLES = [
  {
    title: 'AI & Data Science Roadmap',
    stage: 'For a 12th-grade student',
    steps: ['Choose Science (Maths) stream', 'B.Tech CS / B.Sc Statistics', 'Python + Statistics fundamentals', 'ML certifications & Kaggle projects', 'Data internship', 'Data Analyst → Data Scientist'],
  },
  {
    title: 'Software & IT Roadmap',
    stage: 'For a UG student',
    steps: ['Pick one stack and go deep', 'Build 3–4 real projects', 'Open-source & GitHub profile', 'Internship in 2nd/3rd year', 'Interview & DSA preparation', 'Developer → Senior Developer'],
  },
  {
    title: 'Finance Career Roadmap',
    stage: 'For a commerce student',
    steps: ['Commerce stream with Maths', 'B.Com + CA Foundation', 'Articleship & practical exposure', 'CFA / MBA (based on goal)', 'Analyst roles', 'Senior finance leadership track'],
  },
];

const CareerRoadmaps = () => (
  <main data-testid="career-roadmaps-page">
    <SEO
      title="Career Roadmaps — School to Career Growth, Step by Step | CareerWithRaaj"
      description="Get a personalised career roadmap: School → Stream → Degree → Skills → Certification → Projects → Internship → Placement → Career Growth."
    />
    <PageHero
      overline="Career Roadmaps"
      title="A Step-by-Step Map for Your Career"
      description="Confused about what to study next? Get a clear roadmap based on your current education, interests, target career, and skill level."
    />

    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          index="01"
          overline="The Framework"
          title="School → Career Growth"
          description="Every roadmap we build follows one proven spine — customised to your stage, goals, and pace."
        />
        <div className="mt-16 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-9">
          {ROADMAP_STEPS.map((step, i) => (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.06 * i, duration: 0.5 }}
              className={`flex flex-col items-center gap-3 rounded-2xl border p-5 text-center shadow-card transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-card-hover ${
                i === ROADMAP_STEPS.length - 1 ? 'border-coral bg-navy' : 'border-line bg-white'
              }`}
              data-testid={`framework-step-${step.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className={`font-display text-lg font-bold ${i === ROADMAP_STEPS.length - 1 ? 'text-coral' : 'text-navy/40'}`}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className={`text-xs font-bold uppercase tracking-wider ${i === ROADMAP_STEPS.length - 1 ? 'text-white' : 'text-navy'}`}>
                {step}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-mist py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <SectionHeading
          index="02"
          overline="Examples"
          title="What a Roadmap Looks Like"
          description="Three example roadmaps — yours will be built around your own stage and target career."
        />
        <div className="mt-16 grid gap-6 lg:grid-cols-3">
          {EXAMPLES.map((ex, i) => (
            <Reveal key={ex.title} delay={0.08 * i} className="h-full">
              <div className="flex h-full flex-col rounded-3xl border border-line bg-white p-8 shadow-card transition-[box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:shadow-card-hover" data-testid={`example-roadmap-${i + 1}`}>
                <span className="text-xs font-bold uppercase tracking-wider text-coral">{ex.stage}</span>
                <h3 className="mt-2 font-display text-xl font-semibold text-navy">{ex.title}</h3>
                <ol className="mt-6 flex-1 space-y-3">
                  {ex.steps.map((s, j) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-slate-600">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-navy/5 font-display text-[10px] font-bold text-navy">
                        {j + 1}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white py-24 md:py-32">
      <div className="mx-auto max-w-3xl px-6 md:px-12">
        <Reveal>
          <div className="rounded-3xl border border-line bg-mist p-8 shadow-card md:p-12" data-testid="get-roadmap-form-card">
            <h2 className="font-display text-2xl font-bold text-navy md:text-3xl">Get My Roadmap</h2>
            <p className="mt-3 text-sm text-slate-600 md:text-base">
              Tell us where you are and where you want to go — we'll build your personalised career roadmap.
            </p>
            <div className="mt-8">
              <LeadForm source="roadmap" buttonLabel="Get My Roadmap" interestLabel="Target Career" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <FinalCTA />
  </main>
);

export default CareerRoadmaps;
